import { createFileRoute, Link } from "@tanstack/react-router";
import { isValidElement, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import Markdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  FileQuestion,
  Info,
  LifeBuoy,
  Search,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { cn, slugify } from "@/lib/utils";
import {
  getDocumentationPage,
  listDocumentationPages,
  type PageSommaire,
} from "@/lib/api/documentation";

type DocumentationSearch = {
  page?: string;
};

export const Route = createFileRoute("/_dashboard/admin/documentation")({
  component: DocumentationPage,
  validateSearch: (search: Record<string, unknown>): DocumentationSearch => ({
    page: typeof search.page === "string" && search.page ? search.page : undefined,
  }),
});

// ---------------------------------------------------------------------------
// Lecture du markdown
// ---------------------------------------------------------------------------

/** Texte d'un contenu React, balises retirées : sert aux ancres et aux encadrés. */
function texteBrut(noeud: ReactNode): string {
  if (typeof noeud === "string" || typeof noeud === "number") return String(noeud);
  if (Array.isArray(noeud)) return noeud.map(texteBrut).join("");
  if (isValidElement<{ children?: ReactNode }>(noeud)) return texteBrut(noeud.props.children);
  return "";
}

interface TitreDeSection {
  texte: string;
  ancre: string;
}

/** Les « ## Titres » d'une page, pour le sommaire « Dans cette page ». */
function titresDeSection(contenu: string): TitreDeSection[] {
  const titres: TitreDeSection[] = [];
  let dansBlocDeCode = false;
  for (const ligne of contenu.split("\n")) {
    if (ligne.startsWith("```")) {
      dansBlocDeCode = !dansBlocDeCode;
      continue;
    }
    if (dansBlocDeCode || !ligne.startsWith("## ")) continue;
    const texte = ligne
      .slice(3)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/[*`]/g, "")
      .trim();
    titres.push({ texte, ancre: slugify(texte) });
  }
  return titres;
}

/**
 * Lien entre deux pages. Le markdown désigne l'autre page par son nom de fichier
 * (`10-hausse-tarifaire-par-grilles.md`), ce qui le garde lisible dans GitLab ;
 * l'application en tire l'identifiant de la page.
 */
const LIEN_VERS_UNE_PAGE = /^(?:\.\/)?\d{2}-([a-z0-9-]+)\.md(?:#.*)?$/;

const STYLE_LIEN = "font-medium text-info underline-offset-4 hover:underline dark:text-laposte-teal";

const composants: Components = {
  h2: ({ children }) => (
    <h2
      id={slugify(texteBrut(children))}
      className="mt-10 mb-3 scroll-mt-6 border-b pb-2 text-xl font-semibold first:mt-0"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => <h3 className="mt-6 mb-2 text-base font-semibold">{children}</h3>,
  p: ({ children }) => <p className="my-3 leading-7">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  hr: () => <hr className="my-8" />,
  ul: ({ children, className }) => (
    <ul
      className={cn(
        "my-3 space-y-1.5",
        className?.includes("contains-task-list") ? "list-none pl-0" : "list-disc pl-6"
      )}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => <ol className="my-3 list-decimal space-y-2 pl-6">{children}</ol>,
  li: ({ children }) => <li className="leading-7 marker:text-muted-foreground">{children}</li>,
  // Les cases d'une liste de contrôle se cochent, sans rien enregistrer.
  input: ({ checked }) => (
    <input type="checkbox" defaultChecked={checked} className="mr-2 h-4 w-4 translate-y-0.5 accent-current" />
  ),
  a: ({ href, children }) => {
    const versUnePage = href?.match(LIEN_VERS_UNE_PAGE);
    if (versUnePage) {
      return (
        <Link to="/admin/documentation" search={{ page: versUnePage[1] }} className={STYLE_LIEN}>
          {children}
        </Link>
      );
    }
    // Un écran de l'application ou un site s'ouvre à côté : le mode opératoire
    // reste sous les yeux pendant qu'on le déroule.
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cn(STYLE_LIEN, "inline-flex items-center gap-1")}>
        {children}
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    );
  },
  blockquote: ({ children }) => {
    const texte = texteBrut(children).trimStart();
    const attention = texte.startsWith("Attention");
    const aSavoir = texte.startsWith("À savoir");
    const Icone = attention ? AlertTriangle : Info;
    return (
      <div
        className={cn(
          "my-5 flex gap-3 rounded-lg border-l-4 px-4 py-1",
          attention && "border-warning bg-warning/10",
          aSavoir && "border-info bg-info/10",
          !attention && !aSavoir && "border-border bg-muted/40"
        )}
      >
        {(attention || aSavoir) && (
          <Icone
            className={cn("mt-[18px] h-4 w-4 shrink-0", attention ? "text-laposte-yellow-dark" : "text-info")}
          />
        )}
        <div className="min-w-0">{children}</div>
      </div>
    );
  },
  table: ({ children }) => (
    <div className="my-5 overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/50 text-left">{children}</thead>,
  tr: ({ children }) => <tr className="border-b last:border-b-0">{children}</tr>,
  th: ({ children }) => <th className="px-3 py-2 font-semibold">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 align-top leading-6">{children}</td>,
  code: ({ children }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="my-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  ),
};

// ---------------------------------------------------------------------------
// Sommaire
// ---------------------------------------------------------------------------

/** Pages regroupées par catégorie, dans l'ordre où les catégories apparaissent. */
function parCategorie(pages: PageSommaire[]): [string, PageSommaire[]][] {
  const groupes = new Map<string, PageSommaire[]>();
  for (const page of pages) {
    groupes.set(page.categorie, [...(groupes.get(page.categorie) ?? []), page]);
  }
  return Array.from(groupes.entries());
}

function Sommaire({ pages }: { pages: PageSommaire[] }) {
  const [recherche, setRecherche] = useState("");

  const pagesFiltrees = useMemo(() => {
    const termes = slugify(recherche).split("_").filter(Boolean);
    if (termes.length === 0) return pages;
    return pages.filter((page) => {
      const texte = slugify(`${page.titre} ${page.resume} ${page.categorie}`);
      return termes.every((terme) => texte.includes(terme));
    });
  }, [pages, recherche]);

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Les modes opératoires des administrateurs : une page par tâche, avec les libellés exacts des écrans.
        </p>
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher une page..."
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {pagesFiltrees.length === 0 ? (
        <EmptyState
          icon={FileQuestion}
          title="Aucune page ne correspond"
          description="Essayez un autre mot, ou effacez la recherche pour revoir tout le sommaire."
          action={{ label: "Effacer la recherche", onClick: () => setRecherche("") }}
        />
      ) : (
        parCategorie(pagesFiltrees).map(([categorie, pagesDeLaCategorie]) => (
          <section key={categorie} className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {categorie}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {pagesDeLaCategorie.map((page) => (
                <Link
                  key={page.identifiant}
                  to="/admin/documentation"
                  search={{ page: page.identifiant }}
                  className="group flex items-start justify-between gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary hover:bg-muted/50"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{page.titre}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{page.resume}</p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Une page
// ---------------------------------------------------------------------------

function Article({ identifiant, pages }: { identifiant: string; pages: PageSommaire[] }) {
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ["documentation", identifiant],
    queryFn: () => getDocumentationPage(identifiant),
    retry: false,
  });

  const titres = useMemo(() => (page ? titresDeSection(page.contenu) : []), [page]);

  const position = pages.findIndex((p) => p.identifiant === identifiant);
  const precedente = position > 0 ? pages[position - 1] : null;
  const suivante = position >= 0 && position < pages.length - 1 ? pages[position + 1] : null;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-9 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !page) {
    return (
      <div className="mx-auto max-w-3xl">
        <EmptyState
          icon={FileQuestion}
          title="Page introuvable"
          description="Cette page n'existe pas, ou elle a été renommée."
        />
        <div className="flex justify-center">
          <Button asChild variant="outline">
            <Link to="/admin/documentation" search={{}}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au sommaire
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl gap-10">
      <article className="min-w-0 max-w-3xl flex-1">
        <div className="mb-4">
          <Button asChild variant="ghost" size="sm" className="-ml-3 text-muted-foreground">
            <Link to="/admin/documentation" search={{}}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Sommaire
            </Link>
          </Button>
        </div>

        <Badge variant="secondary">{page.categorie}</Badge>
        <h1 className="mt-3 text-3xl font-bold">{page.titre}</h1>
        {page.resume && <p className="mt-3 text-lg text-muted-foreground">{page.resume}</p>}

        <div className="mt-8">
          <Markdown remarkPlugins={[remarkGfm]} components={composants}>
            {page.contenu}
          </Markdown>
        </div>

        {(precedente || suivante) && (
          <nav className="mt-12 grid gap-3 border-t pt-6 sm:grid-cols-2">
            {precedente ? (
              <Link
                to="/admin/documentation"
                search={{ page: precedente.identifiant }}
                className="rounded-lg border p-4 transition-colors hover:border-primary hover:bg-muted/50"
              >
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ArrowLeft className="h-3 w-3" />
                  Page précédente
                </span>
                <span className="mt-1 block font-medium">{precedente.titre}</span>
              </Link>
            ) : (
              <span />
            )}
            {suivante && (
              <Link
                to="/admin/documentation"
                search={{ page: suivante.identifiant }}
                className="rounded-lg border p-4 text-right transition-colors hover:border-primary hover:bg-muted/50"
              >
                <span className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                  Page suivante
                  <ArrowRight className="h-3 w-3" />
                </span>
                <span className="mt-1 block font-medium">{suivante.titre}</span>
              </Link>
            )}
          </nav>
        )}
      </article>

      {titres.length > 1 && (
        <aside className="hidden w-60 shrink-0 xl:block">
          <div className="sticky top-2 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Dans cette page
            </p>
            <ul className="space-y-1 border-l">
              {titres.map((titre) => (
                <li key={titre.ancre}>
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(titre.ancre)
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                    className="-ml-px block w-full border-l border-transparent py-1 pl-3 text-left text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                  >
                    {titre.texte}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

function DocumentationPage() {
  const { page } = Route.useSearch();
  const zoneDeLecture = useRef<HTMLDivElement>(null);

  const { data: pages, isLoading, isError } = useQuery({
    queryKey: ["documentation"],
    queryFn: listDocumentationPages,
  });

  // Chaque page s'ouvre en haut, quel que soit l'endroit où l'on a cliqué.
  useEffect(() => {
    zoneDeLecture.current?.scrollTo({ top: 0 });
  }, [page]);

  // L'écran occupe la hauteur de la fenêtre et c'est la zone de lecture qui défile :
  // l'en-tête et le sommaire « Dans cette page » restent ainsi visibles.
  return (
    <AdminGuard>
      <div className="flex h-screen flex-col">
        <Header title="Documentation" />

        <div ref={zoneDeLecture} className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="mx-auto max-w-5xl space-y-4">
              <Skeleton className="h-9 w-64" />
              <div className="grid gap-3 md:grid-cols-2">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            </div>
          ) : isError || !pages ? (
            <EmptyState
              icon={LifeBuoy}
              title="La documentation n'a pas pu être chargée"
              description="Rechargez la page. Si le problème persiste, signalez-le au développeur."
            />
          ) : pages.length === 0 ? (
            <EmptyState icon={LifeBuoy} title="Aucune page de documentation" />
          ) : page ? (
            <Article key={page} identifiant={page} pages={pages} />
          ) : (
            <Sommaire pages={pages} />
          )}
        </div>
      </div>
    </AdminGuard>
  );
}
